import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SalesOrdersService } from '@vendasta/sales-orders';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

type NoteType = 'customer' | 'administrator';

type EditingType = 'toggleable' | 'editingonly' | 'disabled';

@Component({
  selector: 'orders-notes',
  templateUrl: 'notes.component.html',
  styleUrls: ['notes.component.scss'],
})
export class NotesComponent implements OnInit, OnDestroy {
  @Input() noteType: NoteType;
  @Input() existingNotes: string;
  @Input() orderId: string;
  @Input() businessId: string;
  @Input() editingType: EditingType = 'toggleable';
  @Input() createMode = false;

  @ViewChild('textFocus') textFocus: ElementRef;

  editMode = false;
  formControl: UntypedFormControl;
  notes: string;
  updatingNotes = false;

  subscriptions: Subscription[] = [];

  constructor(
    private readonly snack: SnackbarService,
    private readonly salesOrdersService: SalesOrdersService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.notes = this.existingNotes;
    this.formControl = new UntypedFormControl(this.existingNotes, []);
    this.subscriptions.push(
      this.formControl.valueChanges.pipe(distinctUntilChanged(), debounceTime(500)).subscribe((notes) => {
        this.notes = notes;
        this.saveNotes();
      }),
    );
    this.editMode = this.editingType === 'editingonly';
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  saveNotes(): void {
    if (this.existingNotes === this.notes || this.createMode) {
      // Skip if there were no changes to the notes
      return;
    }
    this.updatingNotes = true;
    this.updateNotes(this.notes).subscribe({
      next: (successful) => {
        if (successful) {
          this.updatingNotes = false;
          this.existingNotes = this.notes;
        }
      },
      error: () => {
        this.snack.openErrorSnack('COMMON.ORDER_DETAILS.NOTES_UPDATE_ERROR');
        this.updatingNotes = false;
      },
    });
  }

  onBlur(): void {
    this.editMode = this.editingType === 'editingonly';
  }

  onClickEdit(): void {
    this.editMode = true;
    this.cdr.detectChanges();
    this.textFocus.nativeElement.focus();
  }

  updateNotes(existingNotes: string): Observable<boolean> {
    if (this.noteType === 'customer') {
      return this.salesOrdersService.updateCustomerNotes(this.orderId, this.businessId, existingNotes);
    } else {
      return this.salesOrdersService.updateNotes(this.orderId, this.businessId, existingNotes);
    }
  }

  // getNotes can be called from a ViewChild reference to get the current notes value
  getNotes(): string {
    return this.notes;
  }
}
